import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import CalibrationApi from "../../../../../api/CalibrationApi";
import { Ri360AutoCalibrationState } from "../../../../../Model/Calibration";
import { ECarrier } from "../../../../../Model/ECarrier";
import { EModuleType } from "../../../../../Model/EModuleType";
import { ENotificationType } from "../../../../../Model/ENotificationType";
import {
  CarrierState,
  EPsrState,
  ESuiteModeState,
  ESuiteMode,
} from "../../../../../Model/ModuleState";
import { sendNotification } from "../../../../../redux/actions/NotificationAction";
import { getModules } from "../../../../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../../redux/reducers/RootReducer";
import AutoCalibrationUtil from "../../../../../Utility/AutoCalibrationUtil";
import MtiButton from "../../../../Buttons/MtiButton";
import CommonDropDown from "../../../../Common/DropDown/CommonDropDown";
import LayoutContent from "../../../../LayoutContent";
import SettingsNavSubMenu from "../../../SettingsNavSubMenu";
import AutoCalibrationResult from "./AutoCalibrationResult";
import "./AutoCalibrationView.css";
import CalibrationSuccessModal from "./CalibrationSuccessModal";
import CalibrationFailModal from "./CalibrationFailModal";
import CalibrationProgressModal from "./CalibrationProgressModal";
import RunAutoCalibrationModal from "./RunAutoCalibrationModal";
import SlotCorrectionFoundModal from "./SlotCorrectionFoundModal";
import UseManualCalibrationModal from "./UseManualCalibrationModal";
import { CartesianCoordinate } from "../../../../../Model/Coordinate";
import storageView from "../vector-deviation.png";

import { Ri360CarrierCalibration } from "../../../../../Model/StorageConfig";
import PreferenceApi from "../../../../../api/PreferenceApi";
import { SlotCorrectionResult } from "../../../../../Model/SlotCorrection";
import PlateDetectedModal from "./PlateDetectedModal";

type CarrierDisplayValue = {
  display: string;
  value: ECarrier;
  disabled: boolean;
};

type PopupModalState = "IDLE" | "INPROGRESS" | "SUCCESS" | "FAIL";

const getInitialModalState = (
  suiteMode: ESuiteMode,
  psrState: EPsrState,
  autoCalibratingState: ESuiteModeState
): PopupModalState => {
  if (
    suiteMode === ESuiteMode.AutoCalibrating &&
    psrState === EPsrState.Busy &&
    (autoCalibratingState === ESuiteModeState.Started ||
      autoCalibratingState === ESuiteModeState.Stopping)
  ) {
    return "INPROGRESS";
  } else {
    return "IDLE";
  }
};

const getUserIntentionallyStopLocalStorage = (
  autoCalibratingState: ESuiteModeState
) => {
  if (autoCalibratingState === ESuiteModeState.Stopping) {
    const resultString = localStorage.getItem("USER_STOP_AUTO_CALIBRATION");
    return resultString === "true" ? true : false;
  }

  return false;
};

const setUserIntentionallyStopLocalStorage = (value: boolean) => {
  localStorage.setItem("USER_STOP_AUTO_CALIBRATION", value.toString());
};

const getCarrierDisplayValues = (
  carrierStates: CarrierState[],
  autoCalibrationResults: Ri360AutoCalibrationState[]
): CarrierDisplayValue[] => {
  let carriers = AutoCalibrationUtil.getAvailableCarrierSortedWithUpdatedResult(
    carrierStates,
    autoCalibrationResults
  );

  return carriers.map((carrier) => {
    return {
      display: AutoCalibrationUtil.getCarrierName(carrier),
      value: carrier,
      disabled: false,
    };
  });
};

type Props = {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  isCalibrationUpdated: boolean;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
};

export default function AutoCalibrationView({
  setOnApply,
  setOnCancel,
  isCalibrationUpdated,
  setIsCalibrationUpdated,
}: Props) {
  const [showUseManualCalModal, setShowUseManualCalModal] = useState(false);
  const [showRunAutoCalibrationModal, setShowRunAutoCalibrationModal] =
    useState(false);
  const [showSlotCorrectionResultCheck, setShowSlotCorrectionCheck] =
    useState(false);
  const [plateDetectedModal, setPlateDetectedModal] = useState(false)
  const [isSlotCorrectionEnabled, setIsSlotCorrectionEnabled] = useState(false);
  const [slotCorrectionResults, setSlotCorrectionResults] = useState(
    [] as SlotCorrectionResult[]
  );
  const [deleteSlotCorrectionOnApply, setDeleteSlotCorrectionOnApply] =
    useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(
    ECarrier.FixedCarriage
  );
  const [needFetchAutoCalibrationResults, setNeedFetchAutoCalibrationResult] =
    useState(true);
  const [autoCalibrationResults, setAutoCalibrationResults] = useState(
    [] as Ri360AutoCalibrationState[]
  );

  const [storageCalibrations, setStorageCalibrations] = useState(
    [] as Ri360CarrierCalibration[]
  );

  const [carrierStorageCalibration, setCarrierStorageCalibration] =
    useState<Ri360CarrierCalibration | null>(null);

  const onCarrierChanged = (carrier: ECarrier) => {
    setSelectedCarrier(carrier);
    const carrierStorageCalibration = storageCalibrations.filter(
      (c) => c.carrier === carrier
    )[0];
    setCarrierStorageCalibration(carrierStorageCalibration);
  };

  const [carriersSelection, setCarriersSelection] = useState([
    {
      display: AutoCalibrationUtil.getCarrierName(ECarrier.FixedCarriage),
      disabled: false,
      value: ECarrier.FixedCarriage,
    },
  ] as CarrierDisplayValue[]);
  const [vectorDeviation, setVectorDeviation] = useState<CartesianCoordinate>({
    x: 0,
    y: 0,
    z: 0,
  });
  const [vectorDeviations, setVectorDeviations] = useState<{
    [index: string]: CartesianCoordinate;
  }>({});

  const history = useHistory();
  const dispatch = useDispatch();

  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.AutoCalibrating;
  const autoCalibratingState = suiteState?.autoCalibrating;
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrModule = psrModules[0];
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrModule.identifier).psr
  );
  const [showStorageView, setShowStorageView] = useState(false);

  const [popupModalState, setPopupModalState] = useState(
    getInitialModalState(
      suiteMode,
      psrState?.state ?? EPsrState.Unknown,
      autoCalibratingState?.modeState ?? ESuiteModeState.Starting
    )
  );
  const [disableDoneAnimation, setDisableDoneAnimation] = useState(false);

  // need this because user cancel / stop autoCalibration will not show success / failed modal
  // and system will change psrState -> fail when autoCalibration is cancel / stoped
  const [userIntentionallyStop, setUserIntentionallyStop] = useState(
    getUserIntentionallyStopLocalStorage(
      autoCalibratingState?.modeState ?? ESuiteModeState.Starting
    )
  );

  const needShowModalAfterProgress = useRef(popupModalState === "INPROGRESS");

  // popup state changes logic
  useEffect(() => {
    if (suiteMode === ESuiteMode.AutoCalibrating) {
      //show in progress only when psr is busy
      if ((psrState ? psrState.state : EPsrState.Unknown) === EPsrState.Busy) {
        setPopupModalState("INPROGRESS");
        needShowModalAfterProgress.current = true;
        setShowRunAutoCalibrationModal(false);
        setDisableDoneAnimation(false);
      }
    } else if (suiteMode === ESuiteMode.ArmCrashed) {
      setPopupModalState("FAIL");
      needShowModalAfterProgress.current = false;
      setDisableDoneAnimation(true);
    } else if (needShowModalAfterProgress.current) {
      if (psrState?.state === EPsrState.Fail) {
        if (!userIntentionallyStop) {
          setPopupModalState("FAIL");
        } else {
          setPopupModalState("IDLE");
          needShowModalAfterProgress.current = false;
        }
      } else {
        setIsCalibrationUpdated(true);
        setNeedFetchAutoCalibrationResult(true);
        setPopupModalState("SUCCESS");
      }
    }
  }, [psrState?.state, suiteMode, autoCalibratingState]);

  useEffect(() => {
    let carrierDisplayValues = getCarrierDisplayValues(
      psrState ? psrState.carrierStates : ([] as CarrierState[]),
      autoCalibrationResults
    );
    setCarriersSelection(carrierDisplayValues);
    if (isCalibrationUpdated) {
      setSelectedCarrier(carrierDisplayValues[0].value);
    }
  }, [autoCalibrationResults]);

  useEffect(() => {
    if (needFetchAutoCalibrationResults) {
      CalibrationApi.GetAutoCalibrationResult().then(
        (autoCalibrationResults) => {
          setAutoCalibrationResults(autoCalibrationResults);
        }
      );
      setNeedFetchAutoCalibrationResult(false);
    }
  }, [needFetchAutoCalibrationResults]);

  useEffect(() => {
    setOnApply({
      execute: onApply,
      disabled: !isCalibrationUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isCalibrationUpdated,
    });
  }, [isCalibrationUpdated, selectedCarrier]);

  useEffect(() => {
    fetchVectorDeviation();
  }, []);

  useEffect(() => {
    CalibrationApi.getStorageCalibration().then((storageCalibration) => {
      setStorageCalibrations(storageCalibration.ri360StorageCalibrations);
      const carrierStorageCalibration =
        storageCalibration.ri360StorageCalibrations.filter(
          (c) => c.carrier === selectedCarrier
        )[0];
      setCarrierStorageCalibration(carrierStorageCalibration);
    });
  }, []);

  useEffect(() => {
    PreferenceApi.getPreferences().then((values) => {
      const preference = values.find((pf) => pf.name == "PsrPreference");
      if (preference) {
        const enableSlotCorrection = preference.values.find(
          (val) => val.preferenceKey === "PsrPreference.SlotCorrection.Enabled"
        );
        setIsSlotCorrectionEnabled(
          enableSlotCorrection?.preferenceValue === "True"
        );
      }
    });
  }, [isCalibrationUpdated]);

  useEffect(() => {
    CalibrationApi.GetSlotCorrectionResult().then((result) => {
      setSlotCorrectionResults(result);
    });
  }, [isCalibrationUpdated]);

  if (psrState == null) {
    throw new Error("Psr state is null");
  }

  const onApply = () => {
    CalibrationApi.saveStorageAutoCalibration("SUITE", EModuleType.PSR).then(
      (result) => {
        setIsCalibrationUpdated(false);
        setNeedFetchAutoCalibrationResult(true);
        fetchVectorDeviation();
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "The changes has been saved successfully."
          )
        );
      }
    );

    if (isSlotCorrectionEnabled && deleteSlotCorrectionOnApply) {
      CalibrationApi.deleteSlotCorrection(slotCorrectionResults).then(() => {
        CalibrationApi.saveSlotCorrection(psrModule.identifier, []);
      });
    }
  };

  const onCancel = () => {
    CalibrationApi.discardStorageAutoCalibration("SUITE", EModuleType.PSR);
    setIsCalibrationUpdated(false);
    setNeedFetchAutoCalibrationResult(true);
  };

  const stopAutoCalibration = () => {
    setUserIntentionallyStop(true);
    setUserIntentionallyStopLocalStorage(true);
    CalibrationApi.stopStorageAutoCalibration("SUITE", EModuleType.PSR);
  };

  const useManualCalibration = () => {
    CalibrationApi.useManualCalibration("SUITE", EModuleType.PSR, [
      selectedCarrier,
    ]).then((data) => {
      history.push("/settings/vector-editor");
    });
  };

  const onHideDone = useCallback(() => {
    setPopupModalState("IDLE");
    needShowModalAfterProgress.current = false;
  }, []);

  const fetchVectorDeviation = () => {
    CalibrationApi.GetVectorDeviation().then((vectorDeviations) => {
      setVectorDeviations(vectorDeviations);
      console.log("DEBUG", ECarrier[selectedCarrier]);
      setVectorDeviation(vectorDeviations[ECarrier[selectedCarrier]]);
    });
  };

  const processAutoCalibration = () => {
    if (isSlotCorrectionEnabled && slotCorrectionResults.length > 0) {
      setShowSlotCorrectionCheck(true);
    } else {
      setShowRunAutoCalibrationModal(true);
    }
  }

  const handleRunAutoCalibration = () => {
    if(psrState.platePresenceSensor) {
      setPlateDetectedModal(true)
    } else {
      processAutoCalibration()
    }
  }

  const handleIgnorePlateDetected = () => {
    setPlateDetectedModal(false)
    processAutoCalibration()
  }
  const getPitchClassName = (pitch: number) => {
    pitch = Math.abs(pitch);
    if (pitch < 0.01) {
      return "green";
    }

    if (pitch < 0.39) {
      return "orange";
    }

    return "red";
  };

  const getRollClassName = (roll: number) => {
    roll = Math.abs(roll);
    if (roll < 0.01) {
      return "green";
    }

    if (roll < 0.39) {
      return "orange";
    }

    return "red";
  };

  const getYawClassName = (yaw: number) => {
    yaw = Math.abs(yaw);
    if (yaw < 0.01) {
      return "green";
    }

    if (yaw < 0.08) {
      return "orange";
    }

    return "red";
  };

  const pitch = vectorDeviation?.x ?? 0;
  const roll = vectorDeviation?.y ?? 0;
  const yaw = vectorDeviation?.z ?? 0;

  if (carrierStorageCalibration == null) {
    return <div>Loading storage calibration</div>;
  }

  return (
    <div className="auto-calibration-container">
      <div className="auto-calibration-img">
        <img
          src={AutoCalibrationUtil.getCarrierImagePath(selectedCarrier)}
          alt="image"
        />
      </div>
      <div className="auto-calibration-sidepanel">
        <div className="panel-up">
          <div>
            Select one or multiple carriers to auto calibrate:
          </div>
          <MtiButton
            variant="primary"
            colorVariant="alternate"
            onClick={handleRunAutoCalibration}
            disabled={psrState.state === EPsrState.Busy}
            style={{
              width: 250,
            }}
          >
            Run Auto Calibration
          </MtiButton>
        </div>
        <div className="panel-mid">
          <div className="panel-selection">
            {/* carriers */}
            <div className="auto-calibration-command-panel-buttons">
              <div className="auto-calibration-vertical-label-content">
                <label>Selected Carrier</label>
                <CommonDropDown<number>
                  value={selectedCarrier}
                  id="carrier-select"
                  onChange={(value) => {
                    setSelectedCarrier(value as ECarrier);
                    setVectorDeviation(vectorDeviations[ECarrier[value]]);
                  }}
                  style={{
                    width: 186,
                    minHeight: 32,
                  }}
                  dropDownStyle={{
                    width: 186,
                  }}
                  dropDownDisplayValues={carriersSelection}
                />
              </div>

              <div className="auto-calibration-vertical-label-content">
                <label>
                  <br></br>
                </label>

                <MtiButton
                  variant="primary"
                  colorVariant="alternate"
                  onClick={() =>
                    CalibrationApi.exportAutoCalibrationProximity(
                      selectedCarrier,
                      carrierStorageCalibration.footprint,
                      autoCalibrationResults
                    )
                  }
                  disabled={psrState.state === EPsrState.Busy}
                  style={{
                    width: 147,
                    letterSpacing: 0.45,
                    height: 34,
                  }}
                >
                  Download
                </MtiButton>
              </div>
            </div>
            {/* vector deviation */}
            <div className="auto-calibration-vertical-label-content">
              <label>Vector Deviation</label>
              <div className="auto-calibration-vector-deviation">
                <label>Pitch(X)</label>
                <label className={getPitchClassName(pitch)}>
                  {pitch.toFixed(2) + "°"}
                </label>
                <label>Roll(Y)</label>
                <label className={getRollClassName(roll)}>
                  {roll.toFixed(2) + "°"}
                </label>
                <label>Yaw(Z)</label>
                <label className={getYawClassName(yaw)}>
                  {yaw.toFixed(2) + "°"}
                </label>
                <div
                  onMouseEnter={() => setShowStorageView(true)}
                  onMouseLeave={() => setShowStorageView(false)}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14 21.5C18.1421 21.5 21.5 18.1421 21.5 14C21.5 9.85786 18.1421 6.5 14 6.5C9.85786 6.5 6.5 9.85786 6.5 14C6.5 18.1421 9.85786 21.5 14 21.5ZM15.5 10.25C15.5 11.0784 14.8284 11.75 14 11.75C13.1716 11.75 12.5 11.0784 12.5 10.25C12.5 9.42157 13.1716 8.75 14 8.75C14.8284 8.75 15.5 9.42157 15.5 10.25ZM15.5 13.25V18.5H12.5V13.25H15.5Z"
                      fill="#0059CC"
                    />
                  </svg>
                  {showStorageView && (
                    <img alt="storage-view" src={String(storageView)} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <AutoCalibrationResult
            selectedCarrier={selectedCarrier}
            setAutoCalibrationUpdated={setIsCalibrationUpdated}
            autoCalibrationResults={autoCalibrationResults}
            isPsrBusy={psrState.state === EPsrState.Busy}
          />
        </div>
        <div className="panel-down">
          <div className="panel-footer">
            <div>
              <p>
                If you are not sure with the result, adjust the position
                manually using Vector Editor
              </p>
            </div>
            <MtiButton
              variant="primary"
              colorVariant="success"
              onClick={() => {
                setShowUseManualCalModal(true);
              }}
              style={{
                width: 250,
              }}
            >
              Adjust Manually
            </MtiButton>
          </div>
        </div>
      </div>
      <RunAutoCalibrationModal
        show={showRunAutoCalibrationModal}
        onHide={() => {
          setShowRunAutoCalibrationModal(false);
        }}
        onRunAutoCalibration={() => {}}
      />
      <PlateDetectedModal 
        show={plateDetectedModal}
        onClickIgnore={handleIgnorePlateDetected}
        onClickOk={() => setPlateDetectedModal(false)}
      />
      <SlotCorrectionFoundModal
        show={showSlotCorrectionResultCheck}
        onClicNo={() => {
          setDeleteSlotCorrectionOnApply(true);
          setShowSlotCorrectionCheck(false);
          setShowRunAutoCalibrationModal(true);
        }}
        onClickYes={() => {
          setShowSlotCorrectionCheck(false);
          setShowRunAutoCalibrationModal(true);
        }}
      />
      <CalibrationProgressModal
        show={popupModalState === "INPROGRESS"}
        onHide={() => {}}
        stopCalibration={stopAutoCalibration}
        autoCalibratingState={
          autoCalibratingState?.modeState ?? ESuiteModeState.Starting
        }
      />
      <CalibrationSuccessModal
        show={popupModalState === "SUCCESS"}
        onHide={onHideDone}
      />
      <CalibrationFailModal
        show={popupModalState === "FAIL"}
        onHide={onHideDone}
        disableAnimation={disableDoneAnimation}
      />
      <UseManualCalibrationModal
        show={showUseManualCalModal}
        onHide={() => {
          setShowUseManualCalModal(false);
        }}
        doUseManualCalibration={useManualCalibration}
      />
    </div>
  );
}

AutoCalibrationView.whyDidYouRender = true;
